import React, { useCallback, useContext, useRef, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';

import { UniversalCreateButtonLazy as UniversalCreateButton } from '@atlassian/universal-create/button/lazy';
import type { UniversalCreateMenuSection, NamedItems } from '@atlassian/universal-create/types';

import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { fg } from '@confluence/feature-gating';
import { START, END } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import { useCanCreateSpace } from '@confluence/create-space/entry-points/createSpace.ts';
import { AppNavigationContext } from '@confluence/app-navigation-context';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { expVal } from '@confluence/feature-experiments';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import {
	checkShouldShowLozenge,
	iterateLozengeShownTracker,
} from '@confluence/live-pages-utils/entry-points/liveDocsLozengeUtils.ts';
import { usePageSpaceKey } from '@confluence/page-context';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useRouteDataRef } from '@confluence/route-manager/entry-points/RouteState.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';

import { useSpaceEnabledContentTypes } from '../CreateContentPopover/useSpaceEnabledContentTypes.tsx';

import { useUniversalCreateState, useUniversalCreateActions } from './useUniversalCreate.ts';
import { ANALYTICS_EVENT_SOURCE, useMenuItems } from './useMenuItems/index.tsx';
import { useAISmartCreateItem } from './useMenuItems/useAISmartCreateItem.tsx';

export type AppDefinedMenuSection = Extract<
	UniversalCreateMenuSection,
	{ type: 'appDefinedSection' }
>;

const messages = defineMessages({
	create: {
		id: 'create-blank-fabric-page.universal-create.create.text',
		defaultMessage: 'Create',
		description: 'The Create text that appears in the top nav',
	},
});

export const getCreateButtonTheme = (createButtonTheme) => (current, props) => {
	const { buttonStyles, spinnerStyles } = current(props);
	return {
		buttonStyles: {
			...buttonStyles,
			...createButtonTheme.default,
			margin: 0,
			fontWeight: token('font.weight.medium'),
			':hover': createButtonTheme.hover,
			':focus': createButtonTheme.focus,
			// :active doesn't work in FF, because we do a
			// e.preventDefault() on mouse down in Button.
			// '&&' is required to add more CSS specificity
			// && it not a valid CSSObject property
			// @ts-ignore
			'&&': {
				...(props.state === 'active' && createButtonTheme.active),
			},
		},
		spinnerStyles,
	};
};

const Create = ({ flags }) => {
	const { isOpen } = useUniversalCreateState();
	const { setIsOpen, setOpenFrom } = useUniversalCreateActions();
	const menuItems = useMenuItems(
		flags,
		() => setIsOpen(false),
		undefined,
		expVal('navx_universal_create_cwr_split_button', 'isEnabled', false),
		fg('navx_uc_confluence_v2_migration'),
		isOpen,
	);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { match } = useContext(RoutesContext);
	const { theme } = useContext(AppNavigationContext);
	const isOnEditRoute = useIsEditorPage();
	const { formatMessage } = useIntl();
	const { isRovoLLMEnabled, accessStatus, isLoggedIn, cloudId, orgId, userId } = useSessionData();
	const [spaceKey] = usePageSpaceKey();
	const { isLoading, isCreateLiveDocEnabled } = useSpaceEnabledContentTypes(spaceKey);
	const { loading: canCreateSpaceLoading } = useCanCreateSpace();
	const routeDataRef = useRouteDataRef();
	const isLivePage = useIsCurrentPageLive();

	const hasIteratedShownTracker = useRef<boolean>(false);

	// shouldShowLozenge should be stabilized so it is not checked each re-render
	// we also do not want the lozenge to suddenly disappear if it's already shown
	const shouldShowLozenge = useMemo(() => checkShouldShowLozenge(), []);

	const buttonTheme = theme?.mode?.create ? getCreateButtonTheme(theme.mode.create) : undefined;

	const hasAllItemsLoaded = !isLoading && !canCreateSpaceLoading;

	const onCreateClick = useCallback(() => {
		if (!isOpen) {
			if (shouldShowLozenge && isCreateLiveDocEnabled && !hasIteratedShownTracker.current) {
				iterateLozengeShownTracker();
				hasIteratedShownTracker.current = true;
			}
		}

		void setIsOpen(!isOpen);

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'navigationItem',
				actionSubjectId: 'globalCreateContentPopover',
				source: 'globalNavigation',
				attributes: {
					selectedItemPageContext: match?.name,
					navigationLayer: 'global',
					navigationContainer: 'top',
					navVersion: '4',
					navdexPointType: START,
					isOnEditRoute,
				},
			},
		}).fire();
	}, [
		createAnalyticsEvent,
		isOpen,
		setIsOpen,
		match,
		isOnEditRoute,
		shouldShowLozenge,
		isCreateLiveDocEnabled,
	]);

	// Duplicated from useMenuItems hook
	const handleAnalyticsAndResetStickySearch = useCallback(
		(actionSubjectId: string, attributes?: Record<string, string>) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId,
					source: ANALYTICS_EVENT_SOURCE,
					attributes: {
						navdexPointType: END,
						isOnEditRoute,
						currentRouteName: routeDataRef?.current?.routeName,
						currentRouteIsLive: isLivePage,
						...attributes,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, isOnEditRoute, routeDataRef, isLivePage],
	);

	const onSmartCreateClick = useCallback(
		(actionSubjectId: string, attributes?: Record<string, string>) => {
			handleAnalyticsAndResetStickySearch(actionSubjectId, attributes);
			void setIsOpen(false);
		},
		[handleAnalyticsAndResetStickySearch, setIsOpen],
	);

	const smartCreateItem = useAISmartCreateItem({
		source: 'globalCreateDropdown-smartCreate',
		spaceKey,
		onClick: onSmartCreateClick,
	});

	const isAnonymousUser = accessStatus === AccessStatus.ANONYMOUS_ACCESS || !isLoggedIn;
	const isAIFCCreateEnabled = isRovoLLMEnabled && !isAnonymousUser && fg('aifc_create_enabled');

	// Reset the openFrom state when the create menu is closed
	const onClose = () => {
		void setIsOpen(false);
		void setOpenFrom('');
	};

	const universalCreateItemsProps = useMemo(() => {
		const crossAppConfig = {
			enabled: true,
			prefetch: true,
			cloudId: cloudId || '',
			orgId: orgId || '',
			accountId: userId || '',
		};

		if (fg('navx_uc_confluence_v2_migration')) {
			return {
				sections: menuItems.map((section) => ({
					type: 'appDefinedSection',
					...section,
				})) as AppDefinedMenuSection[],
				crossAppCreationConfig:
					hasAllItemsLoaded && !isAnonymousUser && !!cloudId
						? crossAppConfig
						: ({ enabled: false } as const),
			};
		}

		return {
			items: menuItems as NamedItems[],
		};
	}, [menuItems, hasAllItemsLoaded, isAnonymousUser, cloudId, orgId, userId]);

	return (
		<UniversalCreateButton
			{...universalCreateItemsProps}
			onClick={onCreateClick}
			triggerButtonSpotlightTarget="top-nav-create"
			enableTriggerPulse={false}
			isOpen={isOpen}
			onClose={onClose}
			product="confluence"
			buttonTheme={buttonTheme}
			testId="create-content-popover-button"
			shouldRenderToParent
			{...(isAIFCCreateEnabled &&
			expVal('navx_universal_create_cwr_split_button', 'isEnabled', false)
				? { primaryActionItem: smartCreateItem }
				: {})}
		>
			{formatMessage(messages.create)}
		</UniversalCreateButton>
	);
};

const CreateWithFlags = withFlags(Create);

export function UniversalCreate() {
	return (
		<TransparentErrorBoundary attribution={Attribution.NAVX}>
			<CreateWithFlags />
		</TransparentErrorBoundary>
	);
}
